import {Injectable} from "@angular/core";
import {HttpRepository} from "./http.repository";
import {UserSessionRepository} from "./user-session.repository";
import {GlobalConfig} from "../configs/GlobalConfig";
import {Observable, forkJoin, mergeMap} from "rxjs";
import {map, switchMap} from "rxjs/operators";
import {Asset} from "../interfaces/rest/Asset";
import {CommonAsset} from "../interfaces/rest/CommonAsset";
import {PlaceholderAsset} from "../interfaces/rest/PlaceholderAsset";
import {PagingRequest} from "../interfaces/rest/paging/PagingRequest";
import {PagedData} from "../interfaces/rest/paging/PagedData";
import {Filter} from "../interfaces/rest/paging/Filter";
import {AssetTypeTag} from "../interfaces/rest/AssetTypeTag";
import {GraphicsFile} from "../interfaces/rest/GraphicsFile";
import {UserRepository} from "./user.repository";
import {User} from "../interfaces/rest/User";


@Injectable()
export class AssetRepository {

  constructor(private httpRepository: HttpRepository,
              private userRepository: UserRepository,
              private userSessionRepository: UserSessionRepository) {

  }

  /**
   * Get ALL assets.
   * @returns {Observable<Array<Asset>>}
   */
  getAssets(): Observable<Array<Asset>> {
    return this.httpRepository.get<Array<Asset>>(GlobalConfig.endpoints.assets);
  }

  getAssetsWithPaging(pagingRequest: PagingRequest): Observable<PagedData> {
    return this.httpRepository.post(`${GlobalConfig.endpoints.assets}/paging`, pagingRequest);
  }

  getGraphicsAssetsWithPaging(pagingRequest: PagingRequest): Observable<PagedData> {
    const newPagingRequest = PagingRequest.clone(pagingRequest);

    const filter = new Filter();
    filter.property = 'assetType.tags._';
    filter.filterType = 'EQUAL';
    filter.filterValue = AssetTypeTag.GRAPHICS;

    newPagingRequest.filters.push(filter);

    return this.getAssetsWithPaging(pagingRequest);
  }

  getAssetsByCurrentUserWithPaging(pagingRequest: PagingRequest): Observable<PagedData> {
    let currentUser = this.userSessionRepository.getUserSession();
    if (currentUser!.userType === 'see') {
      return this.getAssetsWithPaging(pagingRequest);
    } else {
      return this.userRepository.getUser(Number(currentUser!.userId)).pipe(
        mergeMap((userData: User) => {
          const newPagingRequest = new PagingRequest();
          newPagingRequest.pageSize = pagingRequest.pageSize;
          newPagingRequest.page = pagingRequest.page;
          newPagingRequest.sorts = pagingRequest.sorts;
          newPagingRequest.filters = pagingRequest.filters;
          newPagingRequest.filterGroups = pagingRequest.filterGroups;
          if (userData.lineBarAccess && userData.lineBarAccess.length > 0) {
            const filter = new Filter();
            filter.property = 'line.lineBarConfig.user.userId';
            filter.filterType = 'EQUAL';
            filter.filterValue = currentUser!.userId!.toString();
            newPagingRequest.filters.push(filter);
          } else if (userData.plantAccess && userData.plantAccess.length > 0) {
            const filter = new Filter();
            filter.filterType = 'EQUAL';
            filter.filterValue = currentUser!.userId!.toString();
            filter.property = 'line.plant.plantAccess.user.userId';
            newPagingRequest.filters.push(filter);
          } else {
            const filter = new Filter();
            filter.filterType = 'EQUAL';
            filter.filterValue = currentUser!.clientId;
            filter.property = 'line.plant.ownedBy.companyId';
            newPagingRequest.filters.push(filter);
          }
          return this.getAssetsWithPaging(newPagingRequest);
        }));
    }
  }

  getGraphicsAssetsByCurrentUserWithPaging(pagingRequest: PagingRequest): Observable<PagedData> {
    const newPagingRequest = PagingRequest.clone(pagingRequest);

    const filter = new Filter();
    filter.property = 'assetType.tags._';
    filter.filterType = 'EQUAL';
    filter.filterValue = AssetTypeTag.GRAPHICS;

    newPagingRequest.filters.push(filter);

    return this.getAssetsByCurrentUserWithPaging(newPagingRequest);
  }

  getGraphicsFilesWithPaging(pagingRequest: PagingRequest): Observable<PagedData> {
    return this.httpRepository.post(`${GlobalConfig.endpoints.assets}/graphics/paging`, pagingRequest);
  }

  getGraphicsFilesByCurrentUserWithPaging(pagingRequest: PagingRequest): Observable<PagedData> {
    const currentUser = this.userSessionRepository.getUserSession()!;

    if (currentUser.userType === 'see') {
      return this.getGraphicsFilesWithPaging(pagingRequest);
    } else {
      return this.userRepository.getUser(Number(currentUser.userId)).pipe(
        mergeMap((userData: User) => {
          const newPagingRequest = PagingRequest.clone(pagingRequest);

          if (userData.lineBarAccess && userData.lineBarAccess.length > 0) {
            const filter = new Filter();
            filter.property = 'asset.line.lineBarConfig.user.userId';
            filter.filterType = 'EQUAL';
            filter.filterValue = currentUser!.userId!.toString();
            newPagingRequest.filters.push(filter);
          } else if (userData.plantAccess && userData.plantAccess.length > 0) {
            const filter = new Filter();
            filter.filterType = 'EQUAL';
            filter.filterValue = currentUser!.userId!.toString();
            filter.property = 'asset.line.plant.plantAccess.user.userId';
            newPagingRequest.filters.push(filter);
          } else {
            const filter = new Filter();
            filter.filterType = 'EQUAL';
            filter.filterValue = currentUser!.clientId;
            filter.property = 'asset.line.plant.ownedBy.companyId';
            newPagingRequest.filters.push(filter);
          }
          return this.getGraphicsFilesWithPaging(newPagingRequest);
        }));
    }
  }

  getPlaceholderAssetsWithPaging(pagingRequest: PagingRequest): Observable<PagedData> {
    return this.httpRepository.post(`${GlobalConfig.endpoints.assets}/placeholder-assets/paging`, pagingRequest);
  }

  /**
   * Get all assets for the specified line.
   * @param lineId
   * @returns {Observable<Array<Asset>>}
   */
  getAssetsByLineId(lineId: number): Observable<Array<Asset>> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.lines}/${lineId}/assets`);
  }

  /**
   * Get all common assets with placeholder for the specified line.
   * @param lineId
   * @returns {Observable<Array<Asset>>}
   */
  getCommonAssetsByLineId(lineId: number): Observable<Array<CommonAsset>> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.lines}/${lineId}/common-assets`);
  }

  getAssetsByLineIds(lineIds: number[]): Observable<Array<Array<Asset>>> {
    const observables: Array<Observable<Array<Asset>>> = [];
    lineIds.forEach((lineId) => {
      observables.push(this.getAssetsByLineId(lineId));
    });

    return forkJoin(observables);
  }

  /**
   * Get all assets for the specified plant.
   * @param plantId
   * @returns {Observable<Array<Asset>>}
   */
  getAssetsByPlantId(plantId: number): Observable<Array<Asset>> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.plants}/${plantId}/assets`);
  }

  /**
   * Get all assets for the specified client.
   * @param clientId
   * @param userId (optional) if user id provide filter on line and plant access
   * @returns {Observable<Array<Asset>>}
   */
  getAssetsByClientId(clientId: number, userId: number | undefined = undefined): Observable<Array<Asset>> {
    let endpoint = `${GlobalConfig.endpoints.clients}/${clientId}/assets`;
    if (userId) {
      endpoint += `?userId=${userId}`;
    }
    return this.httpRepository.get(endpoint);
  }

  /**
   * Get all
   * @param assetTypeId
   * @returns {Observable<Array<Asset>>}
   */
  getAssetsByAssetType(assetTypeId: number): Observable<Array<Asset>> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.assets}?assetTypeId=${assetTypeId}`);
  }

  /**
   * Get asset by asset ID.
   * @param assetId
   * @returns {Observable<Asset>}
   */
  getAsset(assetId: number): Observable<Asset> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.assets}/${assetId}`);
  }

  /**
   * Get asset by unique asset TW ID.
   * @param assetId
   * @returns {Observable<Asset>}
   */
  getAssetByUniqueAssetIdThingWorksFk(uniqueAssetIdThingWorksFk: string): Observable<Asset> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.assets}/uniqueAssetIdThingWorksFk/${uniqueAssetIdThingWorksFk}`);
  }

  /**
   * Get placeholder asset by placeholder asset ID.
   * @param assetId
   * @returns {Observable<Asset>}
   */
  getPlaceholderAsset(placeholderAssetId: number): Observable<PlaceholderAsset> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.placeholderAssets}/${placeholderAssetId}`);
  }

  /**
   * Create new asset.
   * @param {Asset} payload
   * @param lineId
   * @returns {Observable<any>}
   */
  addAsset(payload: Asset, lineId: number) {
    return this.httpRepository.post(`${GlobalConfig.endpoints.lines}/${lineId}/assets`, payload);
  }

  /**
   * Create new placeholder asset.
   * @param {Asset} payload
   * @param lineId
   * @returns {Observable<any>}
   */
  addPlaceholderAsset(payload: PlaceholderAsset, lineId: number) {
    return this.httpRepository.post(`${GlobalConfig.endpoints.lines}/${lineId}/placeholder-assets`, payload);
  }

  /**
   * Update existing asset.
   * @param {Asset} payload
   * @param lineId
   * @param assetId
   * @returns {Observable<any>}
   */
  updateAsset(payload: Asset, lineId: number, assetId: number) {
    return this.httpRepository.put(`${GlobalConfig.endpoints.lines}/${lineId}/assets/${assetId}`, payload);
  }

  /**
   * Update existing placeholder asset.
   * @param {PlaceholderAsset} payload
   * @param lineId
   * @param placeholderAssetId
   * @returns {Observable<any>}
   */
  updatePlaceholderAsset(payload: PlaceholderAsset, lineId: number, placeholderAssetId: number) {
    return this.httpRepository.put(`${GlobalConfig.endpoints.lines}/${lineId}/placeholder-assets/${placeholderAssetId}`, payload);
  }

  /**
   * Delete an existing asset.
   * @param assetId
   * @returns {Observable<any>}
   */
  deleteAsset(assetId: number) {
    return this.httpRepository.delete(`${GlobalConfig.endpoints.assets}/${assetId}`);
  }

  /**
   * Delete an existing placeholder asset.
   * @param assetId
   * @returns {Observable<any>}
   */
  deletePlaceholderAsset(assetId: number) {
    return this.httpRepository.delete(`${GlobalConfig.endpoints.assets}/${assetId}`);
  }

  checkAssetIdExists(uniqueAssetId: number): Observable<any> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.assets}/exist/${uniqueAssetId}`);
  }

  checkPlaceholderAssetIdExists(uniquePlaceholderAssetId: number): Observable<any> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.assets}/exist/${uniquePlaceholderAssetId}`);
  }

  /**
   * Reset heat map for specific asset.
   * @param assetId
   * @returns {Observable<Array<Asset>>}
   */
  resetHeatMap(assetId: number) {
    return this.httpRepository.get(`${GlobalConfig.endpoints.assets}/${assetId}/resetheatmap`);
  }

  addGraphicsFile(payload: GraphicsFile): Observable<GraphicsFile> {
    return this.httpRepository.post(GlobalConfig.endpoints.assets + '/graphics/AddorUpdate', payload);
  }

  getGraphicsFile(graphicsFile: GraphicsFile): Observable<GraphicsFile> {
    return this.httpRepository.post(GlobalConfig.endpoints.assets + '/getGraphicsFile', graphicsFile).pipe(
      map((response) => {
        return response;
      })) as Observable<GraphicsFile>
  }

  downloadGraphicsFile(graphicsFileId: any): Observable<GraphicsFile> {
    return this.httpRepository.get(`${GlobalConfig.endpoints.assets}/downloadGraphicsFile/${graphicsFileId}`);
  }

  /**
   * If current user is SEE user, then get ALL assets. If current user is external
   * user, then get all assets belonging to the client of the external user.
   * @returns {Observable<Array<Asset>>}
   */
  getAssetsByCurrentUser(filterByLineAccess = false): Observable<Array<Asset>> {
    if (this.userSessionRepository.getUserSession()?.userType === 'see') {
      console.log('user is see so get all assets');
      return this.getAssets();
    } else {
      return this.userRepository.getUser(this.userSessionRepository.getUserSession()?.userId).pipe(
        switchMap((userData: User) => {
          let clientId = User.getCompanyId(userData);
          if (filterByLineAccess) {
            return this.getAssetsByClientId(clientId!, userData.userId);
          }
          return this.getAssetsByClientId(clientId!, undefined);
        }));
    }
  }
}
